import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import { defineAsyncComponent } from 'vue';
import { BOARDS_DRAWERS } from '@/app/boards/constants';
import { CREATOR_DRAWERS } from '@/app/creators/constants';
import { DASHBOARD_DRAWERS } from '@/app/dashboards/constants';
import { ECOMMERCE_DRAWERS, LIKESHOP_DRAWERS } from '@/app/ecommerce/constants';
import { SCHEDULER_DRAWERS } from '@/app/scheduler/constants';
import { SOCIAL_LISTENING_DRAWERS } from '@/app/socialListening/constants';
import { INTEGRATION_DRAWERS } from '@/app/settings/constants/integrations';
import { SETTINGS_DRAWERS } from '@/app/settings/constants/drawers';
/*
 *   Drawer Registry
 *
 *   Register your drawers here in the section that your drawer belongs, or create a section if
 *   needed and add to the drawerRegistry array below.
 *
 *   Each entry object has the following keys available:
 *   {
 *     name: *Required, unique drawer name
 *     component: *Required, defineAsyncComponent call to dynamically import the component
 *     propsDef: Optional
 *     meta: Optional
 *   }
 *   See dashing-ui useDrawer for details.
 * */
const auth = [
  {
    name: 'startTrial',
    component: defineAsyncComponent(() => import('@/components/InAppTrials/StartTrialPopup.vue')),
  },
];

const boards = [
  {
    name: BOARDS_DRAWERS.DETAIL,
    component: defineAsyncComponent(
      () => import('@/app/boards/components/drawers/BoardDetailDrawer.vue'),
    ),
    propDefs: {
      boardId: {
        type: Number,
      },
      galleryType: {
        type: String,
      },
      isCompetitors: {
        type: Boolean,
      },
    },
  },
  {
    name: BOARDS_DRAWERS.CREATE_BOARD,
    component: defineAsyncComponent(
      () => import('@/app/boards/components/drawers/CreateBoardWizard.vue'),
    ),
    meta: { excludeFromRouter: true },
  },
  {
    name: BOARDS_DRAWERS.GALLERY_REORDER,
    component: defineAsyncComponent(
      () => import('@/app/boards/components/drawers/GalleryReorderDrawer.vue'),
    ),
    meta: { excludeFromRouter: true },
  },
];

const campaigns = [
  {
    name: 'createAffiliateLink',
    component: defineAsyncComponent(
      () => import('@/app/campaigns/drawers/Deliverables/CreateAffiliateLink.vue'),
    ),
  },
  {
    name: 'productOrders',
    component: defineAsyncComponent(
      () => import('@/app/campaigns/drawers/Deliverables/ProductOrders.vue'),
    ),
    propDefs: {
      creatorIds: {
        type: Array,
      },
    },
  },
  {
    name: 'assignDeliverables',
    component: defineAsyncComponent(
      () => import('@/app/campaigns/drawers/Deliverables/AssignDeliverables.vue'),
    ),
  },
  {
    name: 'topPerformingCreators',
    component: defineAsyncComponent(
      () => import('@/app/campaigns/drawers/TopPerformingCreators.vue'),
    ),
  },
  {
    name: 'campaignAffiliateLinks',
    component: defineAsyncComponent(
      () => import('@/app/campaigns/drawers/AffiliateLinks/AffiliateLinksDrawer.vue'),
    ),
  },
  {
    name: 'addCreator',
    component: defineAsyncComponent(
      () => import('@/app/campaigns/drawers/Creators/AddCreatorsWizard.vue'),
    ),
  },
  {
    name: 'creatorDeliverablesDateRangeEditor',
    component: defineAsyncComponent(
      () => import('@/app/campaigns/drawers/Deliverables/DateRangeEditor.vue'),
    ),
  },
  {
    name: 'createEditCampaign',
    component: defineAsyncComponent(
      () => import('@/app/campaigns/drawers/Campaigns/CreateEditCampaign.vue'),
    ),
  },
];

const community = [
  {
    name: 'inboxViewBuilder',
    component: defineAsyncComponent(() => import('@/app/community/drawers/InboxViewBuilder.vue')),
  },
  {
    name: INTEGRATION_DRAWERS.SALESFORCE_INTEGRATION,
    component: defineAsyncComponent(
      () => import('@/app/community/drawers/SalesforceIntegrationDrawer.vue'),
    ),
    meta: {
      excludeFromRouter: true,
      size: 'large',
    },
  },
  {
    name: 'inboxAutomationRules',
    component: defineAsyncComponent(
      () => import('@/app/community/drawers/InboxAutomationRulesDrawer.vue'),
    ),
    meta: {
      excludeFromRouter: true,
      size: 'large',
    },
  },
  {
    name: 'inboxAutomationRuleWizard',
    component: defineAsyncComponent(
      () => import('@/app/community/drawers/InboxAutomationRuleWizard.vue'),
    ),
    meta: {
      excludeFromRouter: true,
    },
  },
];

const creators = [
  {
    name: 'creatorCreatePayorProfile',
    component: defineAsyncComponent(
      () => import('@/app/creators/drawers/payments/CreatePayorProfile.vue'),
    ),
  },
  {
    name: 'creatorCreatePayee',
    component: defineAsyncComponent(
      () => import('@/app/creators/drawers/payments/CreatePayee.vue'),
    ),
  },
  {
    name: 'viewPayment',
    component: defineAsyncComponent(
      () => import('@/app/creators/drawers/payments/ViewPayment.vue'),
    ),
  },
  {
    name: 'creatorManualPayment',
    component: defineAsyncComponent(
      () => import('@/app/creators/drawers/Profile/ManualPayment.vue'),
    ),
  },
  {
    name: 'creatorSendPayment',
    component: defineAsyncComponent(
      () => import('@/app/creators/drawers/payments/SendPayment.vue'),
    ),
  },
  {
    name: 'creatorDeliverablesPosts',
    component: defineAsyncComponent(
      () => import('@/app/creators/drawers/Profile/CreatorDeliverablesPosts.vue'),
    ),
  },
  {
    name: CREATOR_DRAWERS.CREATOR_PROFILE,
    component: defineAsyncComponent(
      () => import('@/app/creators/drawers/Profile/CreatorProfile.vue'),
    ),
  },
  {
    name: CREATOR_DRAWERS.ACCOUNT_PROFILE,
    component: defineAsyncComponent(
      () => import('@/app/creators/drawers/Profile/AccountProfile.vue'),
    ),
  },
  {
    name: CREATOR_DRAWERS.SIMILAR_CREATORS,
    component: defineAsyncComponent(
      () => import('@/app/creators/drawers/Profile/AccountProfile.vue'),
    ),
  },
  {
    name: CREATOR_DRAWERS.BULK_UPLOAD,
    component: defineAsyncComponent(() => import('@/app/creators/drawers/Profile/BulkUpload.vue')),
  },
  {
    name: 'creatorEmailThread',
    component: defineAsyncComponent(
      () => import('@/app/creators/drawers/Profile/CreatorEmailThread.vue'),
    ),
  },
  {
    name: 'emailInvitation',
    component: defineAsyncComponent(
      () => import('@/app/creators/drawers/Profile/EmailInvitation.vue'),
    ),
  },
  {
    name: 'editPayeeStatus',
    component: defineAsyncComponent(
      () => import('@/app/creators/drawers/payments/EditPayeeStatus.vue'),
    ),
  },
  {
    name: 'requestFundDeposit',
    component: defineAsyncComponent(() => import('@/app/creators/drawers/payments/AddFunds.vue')),
  },
  {
    name: 'shopifyCreatorOrderHistory',
    component: defineAsyncComponent(
      () => import('@/app/creators/drawers/shopify/OrderHistory.vue'),
    ),
  },
  {
    name: 'shopifyOrderSummary',
    component: defineAsyncComponent(
      () => import('@/app/creators/drawers/shopify/OrderSummary.vue'),
    ),
  },
  {
    name: CREATOR_DRAWERS.INFLUENTIAL_FANS,
    component: defineAsyncComponent(
      () => import('@/app/creators/drawers/influentialFans/InfluentialFans.vue'),
    ),
  },
  {
    name: CREATOR_DRAWERS.AI_INSIGHTS,
    component: defineAsyncComponent(
      () => import('@/app/creators/drawers/Profile/CreatorAIInsights.vue'),
    ),
  },
];

const dashboards = [
  {
    name: DASHBOARD_DRAWERS.TEMPLATES,
    component: defineAsyncComponent(
      () => import('@/app/dashboards/components/drawers/DashboardTemplates.vue'),
    ),
  },
  {
    name: DASHBOARD_DRAWERS.REPORT_FORM_DRAWER,
    component: defineAsyncComponent(
      () => import('@/app/dashboards/components/drawers/ReportFormDrawer.vue'),
    ),
  },
  {
    name: DASHBOARD_DRAWERS.AUTO_EXPORTS_DRAWER,
    component: defineAsyncComponent(
      () => import('@/app/dashboards/components/drawers/DashboardAutoExportDrawer.vue'),
    ),
  },
  {
    name: DASHBOARD_DRAWERS.NO_REPORT_FORM_DRAWER,
    component: defineAsyncComponent(
      () => import('@/app/dashboards/components/drawers/NoReportFormDrawer.vue'),
    ),
  },
  {
    name: DASHBOARD_DRAWERS.NO_DASHBOARDS,
    component: defineAsyncComponent(
      () => import('@/app/dashboards/components/drawers/NoDashboards.vue'),
    ),
  },
  {
    name: DASHBOARD_DRAWERS.SHARE_DASHBOARD,
    component: defineAsyncComponent(
      () => import('@/app/dashboards/components/drawers/ShareDashboard.vue'),
    ),
  },
  {
    name: DASHBOARD_DRAWERS.ADD_SHAREABLE_LINK,
    component: defineAsyncComponent(
      () => import('@/app/dashboards/components/drawers/AddShareableLinkDrawer.vue'),
    ),
    meta: { excludeFromRouter: true },
  },
  {
    name: DASHBOARD_DRAWERS.EDIT_SHAREABLE_LINK,
    component: defineAsyncComponent(
      () => import('@/app/dashboards/components/drawers/EditShareableLinkDrawer.vue'),
    ),
    meta: { excludeFromRouter: true },
  },
  {
    name: DASHBOARD_DRAWERS.MEDIA_ITEM,
    component: defineAsyncComponent(
      () => import('@/app/dashboards/components/drawers/MediaItemDrawer.vue'),
    ),
  },
  {
    name: DASHBOARD_DRAWERS.DASHBOARD_ACTIONS,
    component: defineAsyncComponent(
      () => import('@/app/dashboards/components/drawers/DashboardDuplicateAndEdit.vue'),
    ),
  },
];

const ecommerce = [
  {
    name: LIKESHOP_DRAWERS.SETTINGS,
    component: defineAsyncComponent(
      () => import('@/app/ecommerce/components/drawers/LikeShopSettingsDrawer.vue'),
    ),
  },
  {
    name: ECOMMERCE_DRAWERS.PRODUCT,
    component: defineAsyncComponent(
      () => import('@/app/ecommerce/components/drawers/ProductDetailDrawerLegacy.vue'),
    ),
  },
  {
    name: ECOMMERCE_DRAWERS.PRODUCT_DETAILS,
    component: defineAsyncComponent(
      () => import('@/app/ecommerce/components/drawers/ProductDetailDrawer.vue'),
    ),
  },
  {
    name: ECOMMERCE_DRAWERS.ADD_PRODUCT,
    component: defineAsyncComponent(
      () => import('@/app/ecommerce/components/drawers/AddProductDrawer.vue'),
    ),
  },
  {
    name: ECOMMERCE_DRAWERS.MEDIA_LINKS,
    component: defineAsyncComponent(
      () => import('@/app/ecommerce/components/drawers/MediaLinksDrawer.vue'),
    ),
  },
  {
    name: ECOMMERCE_DRAWERS.PRODUCT_PAGE_GALLERY_SETTINGS,
    component: defineAsyncComponent(
      () => import('@/app/ecommerce/components/drawers/ProductPageGallerySettingsDrawer.vue'),
    ),
    meta: { excludeFromRouter: true },
  },
];

const library = [
  {
    name: 'media',
    component: defineAsyncComponent(() => import('@/app/library/components/MediaPopup.vue')),
    meta: { pushHistoryOnPropChange: true },
  },
  {
    name: 'selectMedia',
    component: defineAsyncComponent(() => import('@/components/core/SelectMediaDrawer.vue')),
    meta: { excludeFromRouter: true },
  },
  {
    name: 'selectMediaV2',
    component: defineAsyncComponent(
      () => import('@/app/library/components/Media/SelectMediaDrawer.vue'),
    ),
    meta: { excludeFromRouter: true },
  },
  {
    name: 'imageCropper',
    component: defineAsyncComponent(() => import('@/components/ImageCropper.vue')),
    meta: { excludeFromRouter: true },
  },
  {
    name: 'AiAlternatives',
    component: defineAsyncComponent(
      () => import('@/app/library/components/drawers/AiAlternativesDrawer.vue'),
    ),
    propDefs: {
      mediaId: {
        type: Number,
      },
      sourceAdId: {
        type: String,
      },
    },
  },
  {
    name: 'AiAlternativePreview',
    component: defineAsyncComponent(
      () => import('@/app/library/components/drawers/AiAlternativePreviewDrawer.vue'),
    ),
    propDefs: {
      mediaId: {
        type: Number,
      },
      sourceAdId: {
        type: String,
      },
      aiAlternativeMediaId: {
        type: Number,
      },
    },
  },
  {
    name: 'MetaAdsCampaignDetail',
    component: defineAsyncComponent(
      () => import('@/app/library/components/drawers/MetaAdsCampaignDetailDrawer.vue'),
    ),
    propDefs: {
      sourceCampaignId: {
        type: String,
      },
      sourceCampaignName: {
        type: String,
      },
    },
  },
  {
    name: 'MetaAdsAlternativesDrawer',
    component: defineAsyncComponent(
      () => import('@/app/library/components/drawers/MetaAdsAlternativesDrawer.vue'),
    ),
    propDefs: {
      campaignId: {
        type: String,
      },
    },
  },
  {
    name: 'snapchatStory',
    component: defineAsyncComponent(
      () => import('@/app/library/components/drawers/SnapchatStoryDrawer.vue'),
    ),
    propDefs: {
      mediaId: {
        type: [Number, String],
      },
    },
  },
];

const scheduler = [
  {
    name: 'SchedulerEventDrawer',
    component: defineAsyncComponent(() => import('@/app/scheduler/components/EventDrawer.vue')),
  },
  {
    name: SCHEDULER_DRAWERS.EDIT_POST_DRAWER,
    component: defineAsyncComponent(
      () => import('@/app/scheduler/components/EditPost/Layout/EditPostDrawerWrapper.vue'),
    ),
  },
  {
    name: SCHEDULER_DRAWERS.BULK_PINTEREST_DRAWER,
    component: defineAsyncComponent(
      () =>
        import('@/app/scheduler/components/EditPost/DrawerEditors/PinterestBulkDrawerContent.vue'),
    ),
  },
  {
    name: 'schedulerMediaCropper',
    component: defineAsyncComponent(
      () => import('@/components/MediaCropping/ImageCropperDrawer.vue'),
    ),
  },
  {
    name: 'schedulerVideoCropper',
    component: defineAsyncComponent(
      () => import('@/components/MediaCropping/VideoCropperDrawer.vue'),
    ),
  },
  {
    name: 'rssConnectionWizard',
    component: defineAsyncComponent(
      () => import('@/app/scheduler/components/RSS/RSSConnectionWizard.vue'),
    ),
  },
  {
    name: 'rssConnectionList',
    component: defineAsyncComponent(
      () => import('@/app/scheduler/components/RSS/RSSConnectionList.vue'),
    ),
  },
];

const settings = [
  {
    name: 'rssConnectionDrawer',
    component: defineAsyncComponent(
      () => import('@/app/settings/components/RSSConnectionDrawer.vue'),
    ),
  },
  {
    name: 'rssConnectionListDrawer',
    component: defineAsyncComponent(
      () => import('@/app/settings/components/RSSConnectionListDrawer.vue'),
    ),
  },
  {
    name: SETTINGS_DRAWERS.BUSINESS_HOURS,
    component: defineAsyncComponent(() => import('@/app/settings/drawers/BusinessHoursDrawer.vue')),
    meta: {
      excludeFromRouter: true,
    },
  },
];

const socialListening = [
  {
    name: SOCIAL_LISTENING_DRAWERS.TRENDING_KEYWORD,
    component: defineAsyncComponent(
      () => import('@/app/socialListening/drawers/SocialListeningTrendingKeywords.vue'),
    ),
  },
  {
    name: SOCIAL_LISTENING_DRAWERS.TOP_POST,
    component: defineAsyncComponent(
      () => import('@/app/socialListening/drawers/SocialListeningTopPerformingPosts.vue'),
    ),
  },
  {
    name: SOCIAL_LISTENING_DRAWERS.VISUAL_TRENDS,
    component: defineAsyncComponent(
      () => import('@/app/socialListening/drawers/SocialListeningVisualTrends.vue'),
    ),
  },
  {
    name: SOCIAL_LISTENING_DRAWERS.VISUAL_TREND,
    component: defineAsyncComponent(
      () => import('@/app/socialListening/drawers/SocialListeningVisualTrend.vue'),
    ),
  },
  {
    name: SOCIAL_LISTENING_DRAWERS.MANAGE_TOPIC_LIMITS,
    component: defineAsyncComponent(
      () => import('@/app/socialListening/drawers/SocialListeningManageTopicLimits.vue'),
    ),
  },
  {
    name: SOCIAL_LISTENING_DRAWERS.TOP_WEB_RESULTS,
    component: defineAsyncComponent(
      () => import('@/app/socialListening/drawers/SocialListeningTopWebResults.vue'),
    ),
  },
  {
    name: SOCIAL_LISTENING_DRAWERS.WEB_RESULTS,
    component: defineAsyncComponent(
      () => import('@/app/socialListening/drawers/SocialListeningWebResults.vue'),
    ),
  },
  {
    name: SOCIAL_LISTENING_DRAWERS.TOPIC_ALERT,
    component: defineAsyncComponent(
      () => import('@/app/socialListening/drawers/SocialListeningTopicAlert.vue'),
    ),
  },
  {
    name: SOCIAL_LISTENING_DRAWERS.VISUAL_FILTERS,
    component: defineAsyncComponent(
      () => import('@/app/socialListening/drawers/SocialListeningVisualFilters.vue'),
    ),
  },
  {
    name: SOCIAL_LISTENING_DRAWERS.MEDIA_POST,
    component: defineAsyncComponent(
      () => import('@/app/socialListening/drawers/SocialListeningMediaPost.vue'),
    ),
  },
  {
    name: SOCIAL_LISTENING_DRAWERS.TOPIC_TEMPLATE_LIST,
    component: defineAsyncComponent(
      () => import('@/app/socialListening/drawers/SocialListeningTopicTemplateDrawer.vue'),
    ),
  },
  {
    name: SOCIAL_LISTENING_DRAWERS.TOPIC_TEMPLATE_CREATOR,
    component: defineAsyncComponent(
      () => import('@/app/socialListening/drawers/SocialListeningTopicTemplateCreator.vue'),
    ),
  },
  {
    name: SOCIAL_LISTENING_DRAWERS.TOPIC_PREVIEW,
    component: defineAsyncComponent(
      () => import('@/app/socialListening/drawers/SocialListeningTopicPreview.vue'),
    ),
  },
];

export const drawerRegistry = [
  auth,
  boards,
  campaigns,
  community,
  creators,
  dashboards,
  ecommerce,
  library,
  scheduler,
  settings,
  socialListening,
];

export const registerDrawers = () => {
  const { registerDrawer } = useDrawer();
  const registeredDrawerNames = [];
  drawerRegistry.forEach((register) => {
    register.forEach((args) => {
      const { name, component, propDefs = undefined, meta = undefined } = args;
      if (registeredDrawerNames.includes(name)) {
        throw Error(`Drawer name ${name} already registered, name must be unique.`);
      }
      registerDrawer(name, component, propDefs, meta);
      registeredDrawerNames.push(name);
    });
  });
};
